import { Check, Copy } from 'lucide-react';
import * as React from 'react';

import { cn } from '@heidi/utils';

import { Button } from '../button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../tooltip';

import type { CopyButtonProps } from './copy-button.types';
import { useMeasuredWidth } from './hooks/use-measured-width';

// Delay before reverting the label once closing — long enough to outlast Radix's
// close animation, so the revert happens off-screen rather than over the close.
const TOOLTIP_CLOSE_MS = 250;

/**
 * Icon-only button with baked-in copy feedback. On a successful copy the copy
 * glyph pops out (scale + blur) as a check pops in, and the tooltip swaps from
 * `label` to `copiedLabel` — the active label rises in from the bottom and the
 * tooltip width animates to fit it — staying visible for `resetDelayMs` even if
 * the pointer leaves, then both revert. All animation is pure CSS.
 *
 * Presentation-only: the caller's `onCopy` performs the actual clipboard write;
 * this component owns only the visual feedback. Returning `false` (or throwing)
 * from `onCopy` suppresses the animation.
 *
 * The baked-in `Tooltip` relies on the app-global `TooltipProvider`.
 *
 * @default variant 'ghost'
 * @default size 'sm'
 * @default label 'Copy'
 * @default copiedLabel 'Copied'
 */
export const CopyButton = React.forwardRef<HTMLButtonElement, CopyButtonProps>(
  function CopyButton(
    {
      onCopy,
      label = 'Copy',
      copiedLabel = 'Copied',
      resetDelayMs = 2000,
      tooltipSide = 'top',
      iconClassName,
      variant = 'ghost',
      size = 'sm',
      onClick,
      onPointerLeave,
      onBlur,
      'aria-label': ariaLabelProp,
      ...props
    },
    ref
  ) {
    const [isCopied, setIsCopied] = React.useState(false);
    // Hover/focus open intent: set true by Radix's open requests, false by us on
    // pointer-leave / blur (see the controlled Tooltip below).
    const [hoverOpen, setHoverOpen] = React.useState(false);
    // Tooltip label state, decoupled from `isCopied`: follows it to true at once,
    // but reverts only after the tooltip closes (effect below) so the revert
    // never animates over the close.
    const [labelCopied, setLabelCopied] = React.useState(false);
    const timeoutRef = React.useRef<ReturnType<typeof setTimeout>>();

    React.useEffect(() => () => clearTimeout(timeoutRef.current), []);

    const open = isCopied || hoverOpen;
    const activeLabel = isCopied ? copiedLabel : label;

    // Measure both labels from fixed-content sizers, so each state's box width is
    // a known value and the CSS `width` transition animates between them on an
    // in-place swap (a fresh open has no prior width, so it just appears sized).
    // offsetWidth-based — see useMeasuredWidth.
    const [copySizerRef, copyWidth] = useMeasuredWidth();
    const [copiedSizerRef, copiedWidth] = useMeasuredWidth();
    const labelWidth = labelCopied ? copiedWidth : copyWidth;

    // Revert the label after the copied window: in place if still open (hovered),
    // else only once the close has finished — never over the close animation.
    React.useEffect(() => {
      if (isCopied || !labelCopied) return;
      if (hoverOpen) {
        setLabelCopied(false);
        return;
      }
      const id = setTimeout(() => setLabelCopied(false), TOOLTIP_CLOSE_MS);
      return () => clearTimeout(id);
    }, [isCopied, labelCopied, hoverOpen]);

    const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      if (event.defaultPrevented) return;

      let succeeded = true;
      try {
        succeeded = (await onCopy()) !== false;
      } catch {
        succeeded = false;
      }
      if (!succeeded) return;

      setIsCopied(true);
      setLabelCopied(true);
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setIsCopied(false), resetDelayMs);
    };

    // Escape must dismiss the tooltip even mid-confirmation (WCAG 1.4.13 —
    // hover/focus content must be dismissable without moving the pointer or
    // focus). `open` is `isCopied || hoverOpen`, so we clear both — clearing only
    // hover intent would leave it open through the copied window. Radix exposes
    // Escape as its own callback, distinct from the pointer-down/click close that
    // flows through onOpenChange, so honouring it here can't re-trigger the copy.
    const handleEscapeDismiss = () => {
      clearTimeout(timeoutRef.current);
      setIsCopied(false);
      setHoverOpen(false);
    };

    // Icon swap (pure CSS): the stacked glyphs cross-toggle — the outgoing one
    // scales/fades/blurs out as the incoming pops in. `motion-safe:` degrades it
    // to an instant swap under prefers-reduced-motion.
    const pop =
      'motion-safe:transition-all motion-safe:duration-200 motion-safe:ease-out';
    const shown = 'scale-100 opacity-100 blur-0';
    const hidden = 'scale-0 opacity-40 blur-sm';

    // Label slide: "Copied" rises in from the bottom (and falls back out on
    // revert); the resting label gives way upward. Transform + opacity only,
    // motion-safe-gated.
    const labelBase =
      'absolute inset-0 flex items-center justify-center whitespace-nowrap motion-safe:transition-all motion-safe:duration-200 motion-safe:ease-out';

    // One continuous tooltip whose text swaps on copy. Radix's Trigger dismisses
    // on pointer-down/click, which would close + reopen it with the new label;
    // being controlled, we honour only Radix's *open* requests and close on
    // pointer-leave / blur, so `open` never drops mid-copy. `isCopied` holds it
    // open through the confirmation window even after the pointer leaves. Escape
    // is the one close request we do honour (via onEscapeKeyDown below) — it can't
    // re-trigger the copy, and dropping it would break WCAG 1.4.13 dismissal.
    return (
      <Tooltip
        open={open}
        onOpenChange={(next) => {
          if (next) setHoverOpen(true);
        }}
      >
        <TooltipTrigger asChild>
          <Button
            ref={ref}
            iconOnly
            variant={variant}
            size={size}
            aria-label={ariaLabelProp ?? activeLabel}
            onClick={(event) => void handleClick(event)}
            onPointerLeave={(event) => {
              onPointerLeave?.(event);
              setHoverOpen(false);
            }}
            onBlur={(event) => {
              onBlur?.(event);
              setHoverOpen(false);
            }}
            {...props}
          >
            <span className="relative inline-flex items-center justify-center">
              <Copy
                aria-hidden
                className={cn('shrink-0', pop, isCopied ? hidden : shown, iconClassName)}
              />
              <Check
                aria-hidden
                className={cn(
                  'absolute inset-0 m-auto shrink-0',
                  pop,
                  isCopied ? shown : hidden,
                  iconClassName
                )}
              />
            </span>
          </Button>
        </TooltipTrigger>
        {/* Width is set to the active label's measured width (the in-flow sizer
            also sets the line height) and transitions on swap; Radix re-centres
            on resize. overflow-hidden masks the sliding labels at the bottom
            edge. aria-hidden keeps AT (which reads via aria-describedby) on the
            active label. */}
        <TooltipContent side={tooltipSide} onEscapeKeyDown={handleEscapeDismiss}>
          <span
            className="relative block overflow-hidden motion-safe:transition-[width] motion-safe:duration-200 motion-safe:ease-out"
            style={{ width: labelWidth }}
          >
            <span ref={copySizerRef} aria-hidden className="invisible whitespace-nowrap">
              {label}
            </span>
            <span
              ref={copiedSizerRef}
              aria-hidden
              className="invisible absolute whitespace-nowrap"
            >
              {copiedLabel}
            </span>
            {/* Resting label — gives way upward when copied. */}
            <span
              aria-hidden={labelCopied}
              className={cn(
                labelBase,
                labelCopied ? 'opacity-0 motion-safe:-translate-y-full' : 'opacity-100'
              )}
            >
              {label}
            </span>
            {/* Copied label — rises in from the bottom, falls back out the bottom. */}
            <span
              aria-hidden={!labelCopied}
              className={cn(
                labelBase,
                labelCopied ? 'opacity-100' : 'opacity-0 motion-safe:translate-y-full'
              )}
            >
              {copiedLabel}
            </span>
          </span>
        </TooltipContent>
      </Tooltip>
    );
  }
);
